import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sun, Star, Check, Calendar, Bell, ArrowRight, Clock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PRICING_2027, PREMIERE_DEADLINE_LABEL } from '@/data/pricing2027';
import GiftCardSection from '@/components/GiftCardSection';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function Abbonamenti() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { feriale, fullClub } = PRICING_2027.stagionali;

  return (
    <>
      <Helmet>
        <title>Abbonamenti Piscina 2027 | La Villa - Castelfranco Emilia</title>
        <meta name="description" content="Abbonamenti piscina stagione 2027 de La Villa: formule Première Feriale e Full Club, mensili, ingressi giornalieri e pomeridiani. Anteprima con prezzi dedicati." />
      </Helmet>

      <div className="bg-ivory min-h-screen pt-32 md:pt-40" data-testid="abbonamenti-page">
        {/* Header */}
        <section className="pb-16 px-6 md:px-12 lg:px-24">
          <div className="max-w-[1800px] mx-auto text-center">
            <motion.p
              className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Anteprima Stagione 2027
            </motion.p>
            <motion.h1
              className="font-heading text-5xl md:text-6xl text-night tracking-tight mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="page-title"
            >
              Abbonamenti Piscina
            </motion.h1>
            <motion.div
              className="flex items-center justify-center gap-2 mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Calendar className="text-champagne" size={20} />
              <p className="font-body text-lg text-stone-600">
                Date di apertura, orari e condizioni della stagione 2027 <strong className="text-night">in definizione</strong>
              </p>
            </motion.div>
            <motion.p
              className="font-body text-stone-500 max-w-xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Con La Villa Première puoi scegliere in anticipo il tuo abbonamento 2027 a un prezzo dedicato.
              La finestra Première termina il <strong className="text-night">{PREMIERE_DEADLINE_LABEL}</strong>.
            </motion.p>
          </div>
        </section>

        {/* Navigazione interna */}
        <nav
          className="pb-10 px-6 md:px-12 lg:px-24"
          aria-label="Navigazione interna della pagina"
          data-testid="abbonamenti-internal-nav"
        >
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-y border-stone-200 py-4">
            {[
              { label: 'Première', href: '#premiere' },
              { label: 'Mensili', href: '#mensili' },
              { label: 'Ingressi', href: '#ingressi' },
              { label: 'Gift Card', href: '#gift-card' },
              { label: 'FAQ', href: '#faq' },
            ].map(item => (
              <a
                key={item.href}
                href={item.href}
                className="font-body text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-champagne transition-colors"
                data-testid={`nav-${item.href.slice(1)}`}
              >
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Stagionali Première */}
        <section id="premiere" className="py-12 px-6 md:px-12 lg:px-24 scroll-mt-28" data-testid="stagionali-section">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              {/* Feriale */}
              <motion.div
                className="bg-white rounded-2xl overflow-hidden shadow-lg flex flex-col"
                {...fadeUp}
                data-testid="stagionale-feriale-card"
              >
                <div className="bg-gradient-to-r from-stone-800 to-stone-900 text-white p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-2">
                    <Sun size={26} className="text-champagne" />
                    <h2 className="font-heading text-2xl md:text-3xl">Stagionale {feriale.nome}</h2>
                  </div>
                  <p className="font-body text-stone-300">{feriale.giorni}</p>
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-1">
                  <div className="mb-2">
                    <p className="font-body text-xs uppercase tracking-widest text-stone-400 mb-1">Prezzo Première</p>
                    <p className="font-heading text-5xl text-night">{feriale.premiere} €</p>
                  </div>
                  <p className="font-body text-sm text-stone-500 mb-6">
                    Prezzo ordinario, dalla fase successiva: <span className="text-night font-medium">{feriale.ordinario} €</span>
                  </p>
                  <ul className="space-y-2 mb-8">
                    <li className="flex items-center gap-2 font-body text-sm text-stone-600">
                      <Check size={16} className="text-champagne shrink-0" /> {feriale.lettino}
                    </li>
                    <li className="flex items-center gap-2 font-body text-sm text-stone-600">
                      <Check size={16} className="text-champagne shrink-0" /> Accesso piscina dal lunedì al giovedì
                    </li>
                    <li className="flex items-center gap-2 font-body text-sm text-red-600">
                      <Info size={16} className="shrink-0" /> Festivi esclusi
                    </li>
                  </ul>
                  <Button
                    asChild
                    variant="outline"
                    className="mt-auto border-night text-night hover:bg-night hover:text-ivory rounded-full py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                    data-testid="feriale-notify-btn"
                  >
                    <Link to="/estate-2027?interesse=abbonamenti#anteprime">
                      <Bell size={16} className="mr-2" /> Avvisami all'apertura delle vendite
                    </Link>
                  </Button>
                </div>
              </motion.div>

              {/* Full Club — evidenza */}
              <motion.div
                className="bg-night rounded-2xl overflow-hidden shadow-xl border border-champagne/40 flex flex-col"
                {...fadeUp}
                transition={{ delay: 0.12 }}
                data-testid="stagionale-fullclub-card"
              >
                <div className="bg-gradient-to-r from-champagne to-[#d9c58a] text-night p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-2">
                    <Star size={26} className="text-night" />
                    <h2 className="font-heading text-2xl md:text-3xl">Stagionale {fullClub.nome}</h2>
                  </div>
                  <p className="font-body text-night/70">{fullClub.giorni}</p>
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-1">
                  <div className="mb-2">
                    <p className="font-body text-xs uppercase tracking-widest text-stone-500 mb-1">Prezzo Première</p>
                    <p className="font-heading text-5xl text-champagne">{fullClub.premiere} €</p>
                  </div>
                  <p className="font-body text-sm text-stone-400 mb-6">
                    Prezzo ordinario, dalla fase successiva: <span className="text-ivory font-medium">{fullClub.ordinario} €</span>
                  </p>
                  <ul className="space-y-2 mb-8">
                    <li className="flex items-center gap-2 font-body text-sm text-stone-300">
                      <Check size={16} className="text-champagne shrink-0" /> {fullClub.lettino}
                    </li>
                    <li className="flex items-center gap-2 font-body text-sm text-stone-300">
                      <Check size={16} className="text-champagne shrink-0" /> Accesso in tutti i giorni di apertura, weekend e festivi inclusi
                    </li>
                  </ul>
                  <Button
                    asChild
                    className="mt-auto bg-champagne text-night hover:bg-ivory rounded-full py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                    data-testid="fullclub-notify-btn"
                  >
                    <Link to="/estate-2027?interesse=abbonamenti#anteprime">
                      <Bell size={16} className="mr-2" /> Avvisami all'apertura delle vendite
                    </Link>
                  </Button>
                </div>
              </motion.div>
            </div>

            <motion.p className="mt-8 text-center font-body text-xs text-stone-400 max-w-2xl mx-auto leading-relaxed" {...fadeUp}>
              Gli abbonamenti comprendono l'accesso piscina e i servizi indicati nella formula scelta.
              Palestra, esperienze Jacuzzi, acquagym ed eventi serali prevedono condizioni e tariffe dedicate.
            </motion.p>
          </div>
        </section>

        {/* Mensili */}
        <section id="mensili" className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-stone-100 to-white scroll-mt-28" data-testid="mensili-section">
          <div className="max-w-6xl mx-auto">
            <motion.div className="text-center mb-12" {...fadeUp}>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">Flessibilità</p>
              <h2 className="font-heading text-3xl md:text-4xl text-night mb-4">Abbonamenti Mensili</h2>
              <p className="font-body text-stone-500">Perfetti per chi vuole provare o per periodi più brevi. Disponibili durante la stagione.</p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.div
                className="bg-white border-2 border-stone-200 rounded-2xl p-8 hover:border-champagne hover:shadow-xl transition-all"
                {...fadeUp}
                data-testid="mensile-feriale-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-stone-100 rounded-full">
                    <Calendar className="text-champagne" size={24} />
                  </div>
                  <h3 className="font-heading text-2xl text-night">Mensile Feriale</h3>
                </div>
                <p className="font-body text-stone-500 mb-6">Lunedì – Giovedì, festivi esclusi.</p>
                <div className="text-center p-6 bg-stone-50 rounded-xl">
                  <p className="font-heading text-4xl text-night">{PRICING_2027.mensili.feriale} €</p>
                  <p className="font-body text-sm text-stone-500">/ mese</p>
                </div>
              </motion.div>

              <motion.div
                className="bg-white border-2 border-stone-200 rounded-2xl p-8 hover:border-champagne hover:shadow-xl transition-all"
                {...fadeUp}
                transition={{ delay: 0.1 }}
                data-testid="mensile-full-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-stone-100 rounded-full">
                    <Star className="text-champagne" size={24} />
                  </div>
                  <h3 className="font-heading text-2xl text-night">Mensile Full Club</h3>
                </div>
                <p className="font-body text-stone-500 mb-6">Tutti i giorni di apertura della piscina.</p>
                <div className="text-center p-6 bg-stone-50 rounded-xl">
                  <p className="font-heading text-4xl text-night">{PRICING_2027.mensili.fullClub} €</p>
                  <p className="font-body text-sm text-stone-500">/ mese</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Ingressi Giornalieri e Pomeridiani */}
        <section id="ingressi" className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white scroll-mt-28" data-testid="ingressi-section">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-12" {...fadeUp}>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">Tariffe 2027</p>
              <h2 className="font-heading text-3xl md:text-4xl text-night">Ingressi Giornalieri</h2>
            </motion.div>

            <motion.div className="mb-12" {...fadeUp}>
              <h3 className="font-heading text-lg text-night mb-4 text-center">Giornata Intera</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 flex items-center justify-between" data-testid="giornaliero-feriale">
                  <div>
                    <p className="font-heading text-base text-night">Feriale</p>
                    <p className="font-body text-sm text-stone-500">Lunedì – Giovedì</p>
                  </div>
                  <p className="font-heading text-2xl text-champagne">{PRICING_2027.giornalieri.feriale} €</p>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 flex items-center justify-between" data-testid="giornaliero-festivo">
                  <div>
                    <p className="font-heading text-base text-night">Weekend e Festivi</p>
                    <p className="font-body text-sm text-stone-500">Venerdì – Domenica e festivi</p>
                  </div>
                  <p className="font-heading text-2xl text-champagne">{PRICING_2027.giornalieri.festivo} €</p>
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeUp}>
              <h3 className="font-heading text-lg text-night mb-4 text-center">Pomeridiano</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 flex items-center justify-between" data-testid="pomeridiano-feriale">
                  <div>
                    <p className="font-heading text-base text-night">Feriale</p>
                    <p className="font-body text-sm text-stone-500">Lunedì – Giovedì · dalle 15:00</p>
                  </div>
                  <p className="font-heading text-2xl text-champagne">{PRICING_2027.pomeridiani.feriale} €</p>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 flex items-center justify-between" data-testid="pomeridiano-festivo">
                  <div>
                    <p className="font-heading text-base text-night">Weekend e Festivi</p>
                    <p className="font-body text-sm text-stone-500">Venerdì – Domenica e festivi · dalle 16:00</p>
                  </div>
                  <p className="font-heading text-2xl text-champagne">{PRICING_2027.pomeridiani.festivo} €</p>
                </div>
              </div>
            </motion.div>

            <motion.div className="mt-10 flex items-start justify-center gap-2 text-center" {...fadeUp}>
              <Clock size={16} className="text-stone-400 mt-0.5 shrink-0" />
              <p className="font-body text-xs text-stone-400 max-w-xl leading-relaxed">
                Orari di apertura, noleggi e servizi extra della stagione 2027 sono in definizione e saranno
                comunicati sui canali ufficiali prima dell'apertura.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Gift Card + FAQ */}
        <GiftCardSection />

        {/* CTA Estate 2027 */}
        <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-night" data-testid="abbonamenti-estate2027-cta">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div {...fadeUp}>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">Summer 2027 — The Preview</p>
              <h2 className="font-heading text-3xl md:text-4xl text-ivory mb-6">
                Palestra all'aperto, Jacuzzi sotto le stelle, cinema estivo.
              </h2>
              <p className="font-body text-stone-400 mb-8">
                Scopri tutte le novità in preparazione per la prossima stagione e lascia il tuo contatto per ricevere le anteprime.
              </p>
              <Button
                asChild
                className="bg-champagne text-night hover:bg-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="goto-estate2027-btn"
              >
                <Link to="/estate-2027">
                  Scopri l'estate 2027 <ArrowRight className="ml-2" size={16} />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
